import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis
} from "recharts";

import {
  ChartTooltipBox, EmptyState, Panel, axisProps, chartInk, dateLabel, daysLabel, legendFormatter, numberLabel, seriesColor, typeColor
} from "./shared";

const useStyles = makeStyles(theme => ({
  grid: {
    display: "grid", gap: theme.spacing(2), gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" }
  },
  wide: { gridColumn: "1 / -1" }
}));

const StageDuration = ({ rows, types }) => {
  const theme = useTheme();
  const ink = chartInk(theme);
  const presentTypes = types.filter(type => rows.some(row => row.por_tipo[type]));
  if (!rows.length || !presentTypes.length) return <EmptyState>Nenhuma etapa concluída no período.</EmptyState>;
  const data = rows.map(row => presentTypes.reduce((item, type) => ({
    ...item, [type]: row.por_tipo[type]?.media_dias ?? null, [`${type}__n`]: row.por_tipo[type]?.amostras ?? 0
  }), { etapa: row.etapa, numero: String(row.numero).padStart(2, "0") }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -12 }} barGap={2} barCategoryGap="20%">
        <CartesianGrid vertical={false} stroke={ink.grid} />
        <XAxis dataKey="numero" {...axisProps(theme)} />
        <YAxis unit=" d" {...axisProps(theme)} />
        <ChartTooltip cursor={{ fill: theme.palette.action.hover }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={payload[0].payload.etapa} rows={presentTypes.map(type => ({
            label: `${type} (${payload[0].payload[`${type}__n`]})`,
            value: payload[0].payload[type] === null ? "-" : daysLabel(payload[0].payload[type]),
            color: typeColor(theme, types, type)
          }))} />
        ) : null)} />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={legendFormatter(theme)} />
        {presentTypes.map(type => (
          <Bar key={type} dataKey={type} name={type} fill={typeColor(theme, types, type)} radius={[4, 4, 0, 0]} maxBarSize={24} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const StageDelay = ({ rows }) => {
  const theme = useTheme();
  const ink = chartInk(theme);
  if (!rows.length) return <EmptyState>Nenhuma etapa concluída com previsão de término.</EmptyState>;
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 30 + 40)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }} barCategoryGap={6}>
        <CartesianGrid horizontal={false} stroke={ink.grid} />
        <XAxis type="number" unit=" d" {...axisProps(theme)} />
        <YAxis type="category" dataKey="etapa" width={170} {...axisProps(theme)} />
        <ReferenceLine x={0} stroke={ink.axis} />
        <ChartTooltip cursor={{ fill: theme.palette.action.hover }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={payload[0].payload.etapa} rows={[
            { label: "Média (real − previsto)", value: daysLabel(payload[0].payload.media_dias), color: seriesColor(theme, 0) },
            { label: "Etapas consideradas", value: payload[0].payload.amostras }
          ]} />
        ) : null)} />
        <Bar dataKey="media_dias" name="Dias de atraso médio" fill={seriesColor(theme, 0)} radius={4} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const HoursByType = ({ rows }) => {
  const theme = useTheme();
  const ink = chartInk(theme);
  if (!rows.length) return <EmptyState>Nenhum projeto entregue no período.</EmptyState>;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: -12 }} barCategoryGap="30%">
        <CartesianGrid vertical={false} stroke={ink.grid} />
        <XAxis dataKey="tipo" {...axisProps(theme)} />
        <YAxis unit=" h" {...axisProps(theme)} />
        <ChartTooltip cursor={{ fill: theme.palette.action.hover }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={payload[0].payload.tipo} rows={[
            { label: "Média", value: `${numberLabel(payload[0].payload.media_horas)} h`, color: seriesColor(theme, 0) },
            { label: "Projetos entregues", value: payload[0].payload.projetos }
          ]} />
        ) : null)} />
        <Bar dataKey="media_horas" name="Horas médias" fill={seriesColor(theme, 0)} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const TotalDuration = ({ rows }) => {
  const theme = useTheme();
  const ink = chartInk(theme);
  if (!rows.length) return <EmptyState>Nenhum projeto entregue no período.</EmptyState>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: -12 }}>
        <CartesianGrid vertical={false} stroke={ink.grid} />
        <XAxis dataKey="entrega_em" tickFormatter={dateLabel} minTickGap={24} {...axisProps(theme)} />
        <YAxis unit=" d" {...axisProps(theme)} />
        <ChartTooltip cursor={{ stroke: ink.axis }} content={({ active, payload }) => (active && payload?.length ? (
          <ChartTooltipBox title={`#${payload[0].payload.projeto_id} · ${payload[0].payload.cliente}`} rows={[
            { label: "Entrega", value: dateLabel(payload[0].payload.entrega_em) },
            { label: "Prazo total", value: daysLabel(payload[0].payload.dias), color: seriesColor(theme, 0) },
            { label: "Tipo", value: payload[0].payload.tipo || "-" }
          ]} />
        ) : null)} />
        <Line type="linear" dataKey="dias" name="Prazo total" stroke={seriesColor(theme, 0)} strokeWidth={2}
          dot={{ r: 4, strokeWidth: 2, stroke: theme.palette.background.paper, fill: seriesColor(theme, 0) }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
};

const EfficiencyTab = ({ data }) => {
  const classes = useStyles();
  const { efficiency, options } = data;
  return (
    <div className={classes.grid}>
      <Panel className={classes.wide} title="Duração média de cada etapa por tipo de projeto"
        subtitle="Etapas concluídas: do primeiro registro de tempo em execução (ou da abertura) até a conclusão. Entre parênteses, a quantidade de etapas.">
        <StageDuration rows={efficiency.stage_duration} types={options.tipos} />
      </Panel>
      <Panel title="Etapas que mais atrasam" subtitle="Média de dias entre a previsão de término e a conclusão. Valores negativos: concluídas antes do previsto.">
        <StageDelay rows={efficiency.stage_delay} />
      </Panel>
      <Panel title="Horas médias por tipo de projeto" subtitle="Projetos entregues (etapa 12 concluída), somando filhas dos grupos 3 e 2.">
        <HoursByType rows={efficiency.hours_by_type} />
      </Panel>
      <Panel className={classes.wide} title="Prazo total do projeto" subtitle="Dias da abertura da OS pai até a entrega, por data de entrega.">
        <TotalDuration rows={efficiency.total_duration} />
      </Panel>
    </div>
  );
};

export default EfficiencyTab;
